import React, { useEffect, useState } from 'react';
import { useLang } from '../LanguageContext';
import ImageUploadInput from '../components/ImageUploadInput';
import {
  validateMediaFiles,
  uploadMedia,
  getMediaErrorMessage,
} from '../utils/imageUpload';
import { normalizeMediaList } from '../utils/media';

export default function NewsAdminPage() {
  const { t } = useLang();
  const token = localStorage.getItem('token');
  const decodeRole = (tok) => {
    if (!tok) return null;
    try {
      return JSON.parse(atob(tok.split('.')[1])).role;
    } catch {
      return null;
    }
  };
  const role = decodeRole(token);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    title: '',
    body: '',
    images: [],
    imagePublicIds: [],
  });
  const [uploading, setUploading] = useState(false);
  const [edit, setEdit] = useState(null);

  const fetchNews = () => {
    fetch('/api/news')
      .then((res) => res.json())
      .then((d) => setNews(Array.isArray(d) ? d : []))
      .catch((err) => console.error('Failed to fetch news', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchNews();
  }, []);

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!token || files.length === 0) return;
    const err = validateMediaFiles(files);
    if (err) {
      alert(getMediaErrorMessage(err, t));
      e.target.value = '';
      return;
    }
    setUploading(true);
    for (const f of files) {
      try {
        const d = await uploadMedia(f, token);
        setForm((form) => ({
          ...form,
          images: [...form.images, {
            url: d.url,
            publicId: d.publicId,
            type: d.type,
            thumbnailUrl: d.thumbnailUrl,
          }],
          imagePublicIds: [...form.imagePublicIds, d.publicId],
        }));
      } catch (e2) {
        alert(getMediaErrorMessage(e2.message, t));
        break;
      }
    }
    setUploading(false);
    e.target.value = '';
  };

  const removeImage = (idx) => {
    setForm((f) => {
      const images = f.images.slice();
      const ids = f.imagePublicIds.slice();
      images.splice(idx, 1);
      ids.splice(idx, 1);
      return { ...f, images, imagePublicIds: ids };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    const method = edit ? 'PUT' : 'POST';
    const url = edit ? `/api/news/${edit}` : '/api/news';
    await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(form),
    });
    setForm({ title: '', body: '', images: [], imagePublicIds: [] });
    setEdit(null);
    fetchNews();
  };

  const startEdit = (n) => {
    setEdit(n._id);
    setForm({
      title: n.title,
      body: n.body,
      images: normalizeMediaList(n.images || []),
      imagePublicIds: n.imagePublicIds || [],
    });
  };

  const del = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    await fetch(`/api/news/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchNews();
  };

  if (!token || !(role === 'superuser' || role === 'admin')) {
    return (
      <div className="container mt-3">
        <p>{t('login')}...</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mt-3">
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="container mt-3" style={{ maxWidth: 700 }}>
      <h1 className="mb-3">{t('manageNews')}</h1>
      <form onSubmit={submit} className="mb-3">
        <input
          className="form-control mb-2"
          placeholder={t('title')}
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          required
        />
        <textarea
          className="form-control mb-2"
          placeholder={t('content')}
          value={form.body}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
          required
        />
        <ImageUploadInput
          multiple
          className="form-control mb-2"
          onChange={handleFile}
        />
        {uploading && (
          <div className="mb-2">{t('photoUploading')}</div>
        )}
        {form.images.length > 0 && (
          <div className="mb-2">
            {form.images.map((img, i) => {
              const url = img?.url || img;
              if (!url) return null;
              return (
                <div key={img?.publicId || url} className="position-relative d-inline-block me-2 mb-2" style={{ maxWidth: 120 }}>
                  {img?.type === 'video' ? (
                    <video
                      src={url}
                      poster={img.thumbnailUrl || url}
                      controls
                      className="img-fluid"
                    />
                  ) : (
                    <img src={url} alt="" loading="lazy" className="img-fluid" />
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-danger position-absolute top-0 end-0"
                    onClick={() => removeImage(i)}
                  >
                    &times;
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <button className="btn btn-primary" type="submit">
          {edit ? t('save') : t('addNews')}
        </button>
        {edit && (
          <button
            type="button"
            className="btn btn-secondary ms-2"
            onClick={() => {
              setEdit(null);
              setForm({ title: '', body: '', images: [], imagePublicIds: [] });
            }}
          >
            {t('cancel')}
          </button>
        )}
      </form>
      <div className="row">
        {news.map((n) => (
          <div key={n._id} className="col-12 col-md-6 col-lg-4 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <h5 className="card-title d-flex justify-content-between">
                  {n.title}
                  <small className="text-muted">{n.shortId || n._id}</small>
                </h5>
                {n.images && n.images.length > 0 && (
                  <div className="mb-2">
                    {normalizeMediaList(n.images).map((img) => (
                      <div key={img.publicId || img.url} className="d-inline-block me-2 mb-2" style={{ maxWidth: 120 }}>
                        {img.type === 'video' ? (
                          <video
                            src={img.url}
                            poster={img.thumbnailUrl || img.url}
                            controls
                            className="img-fluid"
                          />
                        ) : (
                          <img
                            src={img.url}
                            alt=""
                            loading="lazy"
                            className="img-fluid"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div>
                  <button className="btn btn-sm btn-outline-primary me-1" onClick={() => startEdit(n)}>
                    {t('edit')}
                  </button>
                  <button className="btn btn-sm btn-danger" onClick={() => del(n._id)}>
                    {t('delete')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
